/** TanStack Query hooks – one place for keys, fetchers and invalidation. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, patch, post } from './client'
import type {
  AppSettings, AppUser, AppUserInput, AppUserPatch, AuditEntry, AuthProviders, Category, Customer, DevUser, Event, EventInput, EventSend, EventStats, GenerationResult, Invitation,
  InvitationInput, Me, OutboxDetail, OutboxItem, Preview, Recipients, Registration, Send, SyncRun, Team, Template, Variable,
} from './types'

export const keys = {
  me: ['me'] as const,
  devUsers: ['dev-users'] as const,
  switchUsers: ['switch-users'] as const,
  authProviders: ['auth-providers'] as const,
  users: ['users'] as const,
  settings: ['settings'] as const,
  events: (archived: boolean) => ['events', { archived }] as const,
  event: (id: string) => ['event', id] as const,
  stats: (id: string) => ['event', id, 'stats'] as const,
  categories: (id: string) => ['event', id, 'categories'] as const,
  invitations: (id: string) => ['event', id, 'invitations'] as const,
  invitation: (id: string) => ['invitation', id] as const,
  recipients: (id: string) => ['invitation', id, 'recipients'] as const,
  sends: (id: string) => ['invitation', id, 'sends'] as const,
  registrations: (id: string) => ['event', id, 'registrations'] as const,
  eventSends: (id: string, q: string) => ['event', id, 'sends', q] as const,
  teams: ['teams'] as const,
  customers: (q: string) => ['customers', q] as const,
  syncRuns: ['sync-runs'] as const,
  templates: ['templates'] as const,
  variables: ['variables'] as const,
  audit: (entityId?: string) => ['audit', entityId ?? 'all'] as const,
  outbox: (eventId?: string) => ['outbox', eventId ?? 'all'] as const,
  outboxItem: (id: string) => ['outbox-item', id] as const,
}

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => get<Me>('/api/me'), retry: false })
export const useAuthProviders = () =>
  useQuery({ queryKey: keys.authProviders, queryFn: () => get<AuthProviders>('/api/auth/providers'), staleTime: 5 * 60_000 })
export const useDevUsers = (enabled = true) =>
  useQuery({ queryKey: keys.devUsers, queryFn: () => get<DevUser[]>('/api/dev/users'), enabled, retry: false })
export const useSwitchUsers = (enabled: boolean) =>
  useQuery({ queryKey: keys.switchUsers, queryFn: () => get<DevUser[]>('/api/switch/users'), enabled, retry: false })

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: () => post<void>('/api/auth/logout'), onSuccess: () => qc.clear() })
}
export function useImpersonate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string | null) => (userId ? post<void>('/api/auth/impersonate', { user_id: userId }) : del('/api/auth/impersonate')),
    onSuccess: () => { qc.clear(); qc.invalidateQueries() },
  })
}

export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: () => get<AppUser[]>('/api/users') })
export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (data: AppUserInput) => post<AppUser>('/api/users', data), onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }) })
}
export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: AppUserPatch & { id: string }) => patch<AppUser>(`/api/users/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  })
}
export const useSettings = () => useQuery({ queryKey: keys.settings, queryFn: () => get<AppSettings>('/api/settings') })

export const useEvents = (archived = false) =>
  useQuery({ queryKey: keys.events(archived), queryFn: () => get<Event[]>(`/api/events?include_archived=${archived}`) })
const LIVE = { refetchInterval: 15_000, refetchIntervalInBackground: false } as const

export const useEvent = (id: string) => useQuery({ queryKey: keys.event(id), queryFn: () => get<Event>(`/api/events/${id}`), ...LIVE })
export const useEventStats = (id: string) =>
  useQuery({ queryKey: keys.stats(id), queryFn: () => get<EventStats>(`/api/events/${id}/stats`), ...LIVE })

export function useCreateEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: EventInput) => post<Event>('/api/events', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useUpdateEvent(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<EventInput> & { status?: Event['status'] }) => patch<Event>(`/api/events/${id}`, data),
    onSuccess: (ev) => {
      qc.setQueryData(keys.event(id), ev)
      qc.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

export function useDeleteEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => del(`/api/events/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useDuplicateEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => post<Event>(`/api/events/${id}/duplicate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export const useCategories = (eventId: string) =>
  useQuery({ queryKey: keys.categories(eventId), queryFn: () => get<Category[]>(`/api/events/${eventId}/categories`) })

export function useGenerateCategories(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => post<GenerationResult>(`/api/events/${eventId}/categories/generate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.categories(eventId) })
      qc.invalidateQueries({ queryKey: ['events'] })
      qc.invalidateQueries({ queryKey: keys.event(eventId) })
    },
  })
}

export function useAddManualCategory(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; capacity: number | null }) => post<Category>(`/api/events/${eventId}/categories/manual`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories(eventId) }),
  })
}

export const useInvitations = (eventId: string, enabled = true) =>
  useQuery({ queryKey: keys.invitations(eventId), queryFn: () => get<Invitation[]>(`/api/events/${eventId}/invitations`), enabled })
export const useInvitation = (id: string) =>
  useQuery({ queryKey: keys.invitation(id), queryFn: () => get<Invitation>(`/api/invitations/${id}`) })
export const useRecipients = (id: string) =>
  useQuery({ queryKey: keys.recipients(id), queryFn: () => get<Recipients>(`/api/invitations/${id}/recipients`) })
export const useSends = (id: string, enabled = true) =>
  useQuery({ queryKey: keys.sends(id), queryFn: () => get<Send[]>(`/api/invitations/${id}/sends`), enabled, ...LIVE })
export const useTemplates = () => useQuery({ queryKey: keys.templates, queryFn: () => get<Template[]>('/api/templates') })
export const useVariables = () => useQuery({ queryKey: keys.variables, queryFn: () => get<Variable[]>('/api/variables') })

export function useCreateInvitation(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: InvitationInput) => post<Invitation>(`/api/events/${eventId}/invitations`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(eventId) }),
  })
}

export function useUpdateInvitation(id: string, eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<InvitationInput>) => patch<Invitation>(`/api/invitations/${id}`, data),
    onSuccess: (inv) => {
      qc.setQueryData(keys.invitation(id), inv)
      qc.invalidateQueries({ queryKey: keys.invitations(eventId) })
      qc.invalidateQueries({ queryKey: keys.recipients(id) })
    },
  })
}

export function useDeleteInvitation(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => del(`/api/invitations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(eventId) }),
  })
}

export const previewInvitation = (id: string, body: { customer_id?: string | null; subject?: string; preheader?: string | null; blocks?: unknown[] }) =>
  post<Preview>(`/api/invitations/${id}/preview`, body)

export function useInvitationAction(id: string, eventId: string) {
  const qc = useQueryClient()
  const done = (inv?: Invitation) => {
    if (inv) qc.setQueryData(keys.invitation(id), inv)
    qc.invalidateQueries({ queryKey: keys.invitations(eventId) })
    qc.invalidateQueries({ queryKey: keys.sends(id) })
    qc.invalidateQueries({ queryKey: keys.event(eventId) })
    qc.invalidateQueries({ queryKey: keys.stats(eventId) })
    qc.invalidateQueries({ queryKey: ['events'] })
    qc.invalidateQueries({ queryKey: ['outbox'] })
  }
  return {
    send: useMutation({ mutationFn: () => post<Invitation>(`/api/invitations/${id}/send`), onSuccess: done }),
    schedule: useMutation({
      mutationFn: (scheduled_at: string) => post<Invitation>(`/api/invitations/${id}/schedule`, { scheduled_at }),
      onSuccess: done,
    }),
    unschedule: useMutation({ mutationFn: () => post<Invitation>(`/api/invitations/${id}/unschedule`), onSuccess: done }),
    test: useMutation({
      mutationFn: (body: { to_email: string; customer_id?: string | null }) => post(`/api/invitations/${id}/test`, body),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['outbox'] }),
    }),
  }
}

export const useRegistrations = (eventId: string) =>
  useQuery({ queryKey: keys.registrations(eventId), queryFn: () => get<Registration[]>(`/api/events/${eventId}/registrations`), ...LIVE })

export function useCancelRegistration(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => post<Registration>(`/api/events/${eventId}/registrations/${id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.registrations(eventId) })
      qc.invalidateQueries({ queryKey: keys.stats(eventId) })
      qc.invalidateQueries({ queryKey: keys.event(eventId) })
    },
  })
}

export const useEventSends = (eventId: string, params: { filter?: string; team_id?: string; invitation_id?: string }) => {
  const q = new URLSearchParams()
  if (params.filter && params.filter !== 'all') q.set('filter', params.filter)
  if (params.team_id) q.set('team_id', params.team_id)
  if (params.invitation_id) q.set('invitation_id', params.invitation_id)
  const qs = q.toString()
  return useQuery({ queryKey: keys.eventSends(eventId, qs), queryFn: () => get<EventSend[]>(`/api/events/${eventId}/sends${qs ? `?${qs}` : ''}`), ...LIVE })
}

export const useTeams = () => useQuery({ queryKey: keys.teams, queryFn: () => get<Team[]>('/api/teams') })
export const useCustomers = (q = '', teamId?: string) =>
  useQuery({
    queryKey: keys.customers(`${q}|${teamId ?? ''}`),
    queryFn: () => get<Customer[]>(`/api/customers?q=${encodeURIComponent(q)}${teamId ? `&team_id=${teamId}` : ''}&limit=100`),
  })
export const useSyncRuns = () => useQuery({ queryKey: keys.syncRuns, queryFn: () => get<SyncRun[]>('/api/sync/runs') })

export function useTriggerSync() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => post<SyncRun>('/api/sync'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.teams })
      qc.invalidateQueries({ queryKey: keys.syncRuns })
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

export const useAudit = (entityId?: string) =>
  useQuery({ queryKey: keys.audit(entityId), queryFn: () => get<AuditEntry[]>(`/api/audit${entityId ? `?entity_id=${entityId}` : ''}`) })
export const useOutbox = (eventId?: string) =>
  useQuery({ queryKey: keys.outbox(eventId), queryFn: () => get<OutboxItem[]>(`/api/outbox${eventId ? `?event_id=${eventId}` : ''}`) })
export const useOutboxItem = (id: string | null) =>
  useQuery({ queryKey: keys.outboxItem(id ?? ''), queryFn: () => get<OutboxDetail>(`/api/outbox/${id}`), enabled: !!id })
